import type { Ref } from 'react';
import React, { useCallback, useState } from 'react';
import { Collapsible } from './Collapsible';
import { Tool } from './Tool';
import { LoadMask } from './LoadMask';
import type {
  ChildrenProp,
  ClassNameProp,
  ClosableComponentProps,
  CollapsibleComponentProps,
  DirectionProp,
  MaskableComponentProps,
  ToolableComponentProps
} from '../common-types';
import { Direction } from '../common-types';
import type { ValidIconName } from '..';
import { intl } from '../../lib';

/**
 * Properties for a {@link Panel} component.
 */
export interface BasePanelProps
  extends ClosableComponentProps,
    CollapsibleComponentProps,
    ToolableComponentProps,
    DirectionProp,
    Required<ChildrenProp>,
    ClassNameProp,
    MaskableComponentProps {
  /**
   * Panel title.
   */
  title?: string | React.JSX.Element;
  /**
   * Controls whether the panel title wraps or truncates with ellipsis.
   */
  breakLineHeader?: boolean;
  /**
   * Panel actions.
   */
  actions?: React.ReactNode;

  /**
   * Indicates if this panel should become a main panel.
   */
  mainPanel?: boolean;

  /**
   * The heading level of the Panel's title.
   */
  titleHeadingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /**
   * Highlights the panel title with a colored left border, using the given css color.
   */
  titleHighlight?: string;

  /**
   * Makes panel content section tabbable with tabindex=0.
   */
  tabbableContent?: true;

  /**
   * Role attribute for the panel.
   */
  role?: string;

  /**
   * Aria-modal attribute for the panel.
   */
  'aria-modal'?: boolean;
}

/**
 * Properties for a {@link Panel} component with collapsible=true.
 */
export interface CollapsiblePanelProps extends BasePanelProps {
  /**
   * Component is collapsible.
   */
  collapsible: true;
  /**
   * Aria-label to set for the Collapsible.
   */
  'aria-label': string;
}

/**
 * Properties for a {@link Panel} component with collapsible=false|undefined.
 */
export interface NonCollapsiblePanelProps extends BasePanelProps {
  /**
   * Component is not collapsible.
   */
  collapsible?: false;
  /**
   * Aria-label should not be defined if collapsible=false|undefined
   */
  'aria-label'?: undefined;
}

/**
 * Props for a regular panel.
 * Union type for CollapsiblePanelProps and NonCollapsiblePanelProps properties.
 */
export type PanelProps = CollapsiblePanelProps | NonCollapsiblePanelProps;

/**
 * Props for a panel, which uses a <form> HTML element.
 */
export type FormPanelProps = PanelProps &
  Partial<
    Omit<
      React.DetailedHTMLProps<
        React.FormHTMLAttributes<HTMLFormElement>,
        HTMLFormElement
      >,
      'ref'
    >
  >;

/**
 * Helper type to determine if PanelProps or FormPanelProps are expected, depending on the variant.
 */
type PanelOrFormPanel<
  DomElement extends 'form' | 'div'
> = DomElement extends 'form' ? FormPanelProps : PanelProps;

/**
 * Panel component implementation.
 *
 * @param props the {@link PanelProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
function PanelComponent<DomElement extends 'form' | 'div'>({
  title,
  collapsible = false,
  breakLineHeader = false,
  openInitially = true,
  closable = false,
  children,
  tools,
  actions,
  className,
  direction,
  masked = false,
  mainPanel = false,
  collapsed,
  setCollapsed,
  domElementType,
  tabbableContent,
  titleHeadingLevel: HeadingTag = 'h2',
  titleHighlight,
  ...props
}: PanelOrFormPanel<DomElement> & { domElementType: DomElement } & {
  ref?: Ref<HTMLDivElement | HTMLFormElement>;
}): React.JSX.Element {
  // callback to determine which classes to set when the component is collapsed
  // these classes will be returned as an array
  const determineCollapsedClasses = useCallback(() => {
    return direction ? ['collapsed', `collapsed-${direction}`] : ['collapsed'];
  }, [direction]);

  // css classes that will be added to the panel if it's collapsed
  const [collapsedClasses, setCollapsedClasses] = useState<Array<string>>(
    openInitially ? [] : determineCollapsedClasses()
  );
  const closeTool = closable && (
    <Tool
      icon="shui-cancel"
      onClick={closable.onClose}
      tooltip={intl('view.button.close')}
    />
  );
  // the closeTool should always be the last tool
  const toolsElement = (
    <>
      {tools}
      {closeTool}
    </>
  );
  // content inside the panel
  const panelContentElement = (
    <div className="panel-content" tabIndex={tabbableContent && 0}>
      {children}
    </div>
  );
  // panel actions
  const actionsElement = actions ? (
    <div className="actions">{actions}</div>
  ) : (
    <></>
  );

  // handle collapsible state
  const onExpand = useCallback((): void => {
    setCollapsedClasses([]);
  }, []);

  const onCollapse = useCallback((): void => {
    setCollapsedClasses(determineCollapsedClasses());
  }, [determineCollapsedClasses]);

  // when the collapsed state is controlled
  const collapsedControlled = collapsed !== undefined && setCollapsed;
  const getControlledCollapsedClasses = useCallback((): string[] => {
    let controlledCollapsedClasses: string[] = [];
    if (collapsedControlled && collapsed) {
      controlledCollapsedClasses = determineCollapsedClasses();
    }
    return controlledCollapsedClasses;
  }, [collapsed, collapsedControlled, determineCollapsedClasses]);

  // get collapsible icons
  let collapsibleOpenIcon: ValidIconName | undefined = undefined,
    collapsibleClosedIcon: ValidIconName | undefined = undefined;
  if (direction && direction === Direction.LEFT) {
    collapsibleOpenIcon = 'arrow-line-left-s';
    collapsibleClosedIcon = 'arrow-line-right-s';
  } else if (direction && direction === Direction.RIGHT) {
    collapsibleOpenIcon = 'arrow-line-right-s';
    collapsibleClosedIcon = 'arrow-line-left-s';
  } // else just use the defaults

  // the panel classes
  const panelClasses = ['ehs-panel'];
  // determine if it is a main panel, if so add  only a certain class.
  if (mainPanel) panelClasses.push('main-panel');
  if (collapsedControlled) {
    panelClasses.push(...getControlledCollapsedClasses());
  } else {
    panelClasses.push(...collapsedClasses);
  }
  if (className) panelClasses.push(className);

  const innerElement = (
    <>
      {collapsible && title ? (
        <Collapsible
          triggerText={title}
          triggerSibling={toolsElement}
          openInitially={openInitially}
          onCollapse={onCollapse}
          onExpand={onExpand}
          openIcon={collapsibleOpenIcon}
          collapsedIcon={collapsibleClosedIcon}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          aria-label={props['aria-label'] as string}
        >
          {panelContentElement}
          {actionsElement}
        </Collapsible>
      ) : (
        <>
          {(title || tools) && (
            <div
              className="panelTitle"
              style={
                titleHighlight
                  ? { borderLeft: `3px solid ${titleHighlight}` }
                  : undefined
              }
            >
              {title && (
                <HeadingTag
                  className={
                    breakLineHeader
                      ? 'panelTitle-text break-line'
                      : 'panelTitle-text ellipsis'
                  }
                >
                  {title}
                </HeadingTag>
              )}
              <div className="tools">{toolsElement}</div>
            </div>
          )}
          {panelContentElement}
          {actionsElement}
        </>
      )}
      <LoadMask masked={masked} />
    </>
  );

  return domElementType === 'form' ? (
    <form
      {...props}
      ref={props.ref as Ref<HTMLFormElement>}
      className={panelClasses.join(' ')}
    >
      {innerElement}
    </form>
  ) : (
    <div
      {...props}
      ref={props.ref as Ref<HTMLDivElement>}
      className={panelClasses.join(' ')}
      aria-modal={props['aria-modal']}
    >
      {innerElement}
    </div>
  );
}

/**
 * A simple panel component.
 *
 * @param props the {@link PanelProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function Panel(
  props: Readonly<PanelProps> & {
    ref?: Ref<HTMLDivElement>;
  }
): React.JSX.Element {
  return <PanelComponent domElementType="div" {...props} />;
}

/**
 * A panel component, which uses a <form> element.
 * This is for use-cases where things like a managedForm's submit button go into the `actions`, etc.
 *
 * @param props the {@link FormPanelProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function FormPanel(
  props: Readonly<FormPanelProps> & {
    ref?: Ref<HTMLFormElement>;
  }
): React.JSX.Element {
  return <PanelComponent domElementType="form" {...props} />;
}
